import * as Event from '@/App/Event';
import ChalkySticks from '@chalkysticks/sdk';
import Store from '@/Store';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { v4 as uuidv4 } from 'uuid';

/**
 * @class PushNotification
 * @package App/Service
 * @project ChalkySticks App
 */
export class PushNotification {
	/**
	 * Check if push notifications are enabled in the system
	 *
	 * @return Promise<boolean>
	 */
	public static async hasPermission(): Promise<boolean> {
		if (!Capacitor.isNativePlatform()) {
			return false;
		}

		const result = await FirebaseMessaging.checkPermissions();
		return result.receive === 'granted';
	}

	/**
	 * Request push notification permissions
	 *
	 * @return Promise<boolean> True if permissions were granted
	 */
	public static async requestPermission(): Promise<boolean> {
		if (!Capacitor.isNativePlatform()) {
			return false;
		}

		const result = await FirebaseMessaging.requestPermissions();
		return result.receive === 'granted';
	}

	/**
	 * Register for push notifications.
	 * This will request permissions if needed, register with the push service,
	 * and set up the event listeners.
	 *
	 * @return Promise<boolean> True if registration was successful
	 */
	public static async register(): Promise<boolean> {
		if (!Capacitor.isNativePlatform()) {
			ChalkySticks.Utility.Log.debug('push-notification', 'Push notifications not supported on web platform.');
			return false;
		}

		// Check if we already have permission
		if (!(await this.hasPermission())) {
			ChalkySticks.Utility.Log.debug('push-notification', 'Requesting push notification permission.');

			// Prompts user with native dialog to accept or deny
			const granted = await this.requestPermission();

			if (!granted) {
				ChalkySticks.Utility.Log.debug('push-notification', 'Push permission denied by OS.');
				return false;
			}

			ChalkySticks.Utility.Log.debug('push-notification', 'Push permission granted by OS.');
		}

		try {
			ChalkySticks.Utility.Log.debug('push-notification', 'Registering with Firebase.');

			// Set up listeners
			this.setupListeners();

			// Get the FCM token directly
			const { token } = await FirebaseMessaging.getToken();

			if (token) {
				await this.handleTokenReceived(token);
			}

			ChalkySticks.Utility.Log.debug('push-notification', 'Registration complete.');

			return true;
		} catch (error) {
			ChalkySticks.Utility.Log.error('push-notification', 'Failed to register for push notifications:', error);
			return false;
		}
	}

	/**
	 * Handle receiving a new FCM token
	 *
	 * @private
	 * @param string token
	 * @return Promise<void>
	 */
	private static async handleTokenReceived(token: string): Promise<void> {
		try {
			const appInfo = await App.getInfo();
			const appVersion = appInfo.version;
			const platform = Capacitor.getPlatform();
			const deviceId = await this.getDeviceId();

			ChalkySticks.Utility.Log.debug('push-notification', 'FCM token:', token);
			ChalkySticks.Utility.Log.debug('push-notification', 'Device info:', {
				appVersion,
				deviceId,
				platform,
			});

			// Save token to store and send to backend
			Store.dispatch('settings/registerPushToken', {
				appVersion: appVersion,
				deviceId: deviceId,
				platform: platform,
				token: token,
			});

			// Dispatch event
			ChalkySticks.Core.Event.Bus.dispatch(Event.PushNotification.Registered, {
				deviceId: deviceId,
				platform: platform,
				token: token,
			});
		} catch (error) {
			ChalkySticks.Utility.Log.error('push-notification', 'Error handling token:', error);

			const platform = Capacitor.getPlatform();

			Store.dispatch('settings/registerPushToken', {
				appVersion: '1.0.0',
				platform: platform,
				token: token,
			});

			ChalkySticks.Core.Event.Bus.dispatch(Event.PushNotification.Registered, {
				platform: platform,
				token: token,
			});
		}
	}

	/**
	 * Set up all the push notification event listeners
	 *
	 * @private
	 */
	private static setupListeners(): void {
		// Remove any existing listeners to prevent duplicates
		this.removeListeners();

		// Token refresh listener
		FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
			ChalkySticks.Utility.Log.debug('push-notification', 'Token refreshed:', token);
			await this.handleTokenReceived(token);
		});

		// Notification received while app is in foreground
		FirebaseMessaging.addListener('notificationReceived', (notification) => {
			ChalkySticks.Utility.Log.debug('push-notification', 'Notification received:', notification);

			ChalkySticks.Core.Event.Bus.dispatch(Event.PushNotification.Received, {
				notification: notification,
			});
		});

		// Notification action performed (user tapped notification)
		FirebaseMessaging.addListener('notificationActionPerformed', (action) => {
			ChalkySticks.Utility.Log.debug('push-notification', 'Notification action performed:', action);

			ChalkySticks.Core.Event.Bus.dispatch(Event.PushNotification.Opened, {
				action: action,
			});
		});
	}

	/**
	 * Remove all push notification listeners
	 *
	 * @private
	 */
	private static removeListeners(): void {
		FirebaseMessaging.removeAllListeners();
	}

	/**
	 * Get a unique device identifier that persists across app restarts.
	 * Uses UUID v4 format stored in localStorage.
	 *
	 * @private
	 * @return Promise<string> A UUID v4 device identifier
	 */
	private static async getDeviceId(): Promise<string> {
		const storageKey = 'cs-device-id';
		const storedId = localStorage.getItem(storageKey);

		// Validate existing UUID format (UUID v4)
		const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

		// Log stored ID for debugging
		ChalkySticks.Utility.Log.debug('push-notification', 'Checking stored device ID:', storedId);

		if (storedId && uuidRegex.test(storedId)) {
			return storedId;
		}

		// Generate new UUID using uuidv4() or fallback
		const newId = this.generateUUID();
		localStorage.setItem(storageKey, newId);

		return newId;
	}

	/**
	 * Generate a UUID v4.
	 *
	 * @private
	 * @return string
	 */
	private static generateUUID(): string {
		return (
			uuidv4() ||
			'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
				const r = (Math.random() * 16) | 0;
				const v = c === 'x' ? r : (r & 0x3) | 0x8;
				return v.toString(16);
			})
		);
	}

	/**
	 * Re-register the stored push token with the backend.
	 * Called on login and app resume to associate/refresh user account.
	 * If no token is stored but permission is granted, triggers a fresh registration.
	 *
	 * @return Promise<void>
	 */
	public static async refreshRegistration(): Promise<void> {
		const storedToken = Store.getters['settings/pushToken'];
		const storedDeviceId = Store.getters['settings/deviceId'];

		// If no stored token, check if we have permission and can re-register
		if (!storedToken) {
			const hasPermission = await this.hasPermission();

			if (hasPermission) {
				ChalkySticks.Utility.Log.debug(
					'push-notification',
					'No stored token but have permission, re-registering.',
				);
				await this.register();
			} else {
				ChalkySticks.Utility.Log.debug('push-notification', 'No stored token and no permission.');
			}

			return;
		}

		try {
			// Get fresh FCM token (it may have changed)
			const { token } = await FirebaseMessaging.getToken();
			const appInfo = await App.getInfo();
			const appVersion = appInfo.version;
			const platform = Capacitor.getPlatform();
			const deviceId = storedDeviceId || (await this.getDeviceId());

			ChalkySticks.Utility.Log.debug(
				'push-notification',
				'Refreshing push registration with deviceId:',
				deviceId,
			);

			Store.dispatch('settings/registerPushToken', {
				appVersion: appVersion,
				deviceId: deviceId,
				platform: platform,
				token: token,
			});
		} catch (error) {
			ChalkySticks.Utility.Log.error('push-notification', 'Failed to refresh registration:', error);
		}
	}

	/**
	 * Remove the device token from the backend on logout.
	 * Sends DELETE to /push-notification/{device_id}
	 *
	 * @return Promise<void>
	 */
	public static async unregister(): Promise<void> {
		const deviceId = Store.getters['settings/deviceId'] || localStorage.getItem('cs-device-id');

		if (!deviceId) {
			ChalkySticks.Utility.Log.debug('push-notification', 'No device ID to unregister.');
			return;
		}

		try {
			ChalkySticks.Utility.Log.debug('push-notification', 'Unregistering push token for device:', deviceId);

			const model = new ChalkySticks.Core.Model.Base();
			model.setId(deviceId);
			model.setEndpoint('/push-notification');
			await model.delete();

			// Optionally delete the FCM token from Firebase
			await FirebaseMessaging.deleteToken();

			// Clear stored token
			Store.commit('settings/setPushToken', null);

			ChalkySticks.Utility.Log.debug('push-notification', 'Successfully unregistered push token.');
		} catch (error) {
			ChalkySticks.Utility.Log.error('push-notification', 'Failed to unregister push token:', error);
		}
	}

	/**
	 * Get delivered notifications
	 *
	 * @return Promise<any[]>
	 */
	public static async getDeliveredNotifications(): Promise<any[]> {
		const result = await FirebaseMessaging.getDeliveredNotifications();
		return result.notifications;
	}

	/**
	 * Remove all delivered notifications
	 *
	 * @return Promise<void>
	 */
	public static async removeAllDeliveredNotifications(): Promise<void> {
		return FirebaseMessaging.removeAllDeliveredNotifications();
	}
}

export default PushNotification;
